import { Resources } from "../resources";

/**
 * AudioManager - Singleton for managing game audio
 * 
 * Handles SFX and Music volume control with localStorage persistence.
 */
class AudioManagerClass {
    private sfxVolume: number = 1.0;
    private musicVolume: number = 0.5;
    private hitSounds = [
        Resources.Hit1,
        Resources.Hit2,
        Resources.Hit3,
        Resources.Hit4,
    ];

    constructor() {
        this.loadSettings();
    }

    /**
     * Load settings from localStorage
     */
    private loadSettings(): void {
        try {
            const saved = localStorage.getItem('audioSettings');
            if (saved) {
                const settings = JSON.parse(saved);
                this.sfxVolume = settings.sfxVolume ?? 1.0;
                this.musicVolume = settings.musicVolume ?? 0.5;
            }
        } catch (e) {
            // Ignore localStorage errors
        }
    }

    /**
     * Save settings to localStorage
     */
    private saveSettings(): void {
        try {
            localStorage.setItem('audioSettings', JSON.stringify({
                sfxVolume: this.sfxVolume,
                musicVolume: this.musicVolume,
            }));
        } catch (e) {
            // Ignore localStorage errors
        }
    }

    /**
     * Play a random hit sound effect
     */
    public playHitSound(): void {
        if (this.sfxVolume <= 0) return;

        const sound = this.hitSounds[Math.floor(Math.random() * this.hitSounds.length)];
        sound.play(this.sfxVolume);
    }

    /**
     * Get current SFX volume (0-1)
     */
    public getSfxVolume(): number {
        return this.sfxVolume;
    }

    /**
     * Set SFX volume (0-1)
     */
    public setSfxVolume(volume: number): void {
        this.sfxVolume = Math.max(0, Math.min(1, volume));
        this.saveSettings();
    }

    /**
     * Get current music volume (0-1)
     */
    public getMusicVolume(): number {
        return this.musicVolume;
    }

    /**
     * Set music volume (0-1) and apply to playing music
     */
    public setMusicVolume(volume: number): void {
        this.musicVolume = Math.max(0, Math.min(1, volume));
        this.saveSettings();

        // Apply to currently playing music
        if (Resources.MenuMusic.isPlaying()) {
            Resources.MenuMusic.volume = this.musicVolume;
        }
        if (Resources.GameMusic.isPlaying()) {
            Resources.GameMusic.volume = this.musicVolume;
        }
    }

    /**
     * Play menu music at current volume
     */
    public playMenuMusic(): void {
        if (!Resources.MenuMusic.isPlaying()) {
            Resources.MenuMusic.loop = true;
            Resources.MenuMusic.play(this.musicVolume);
        }
    }

    /**
     * Play game music at current volume
     */
    public playGameMusic(): void {
        if (!Resources.GameMusic.isPlaying()) {
            Resources.GameMusic.loop = true;
            Resources.GameMusic.play(this.musicVolume);
        }
    }

    /**
     * Stop all music
     */
    public stopAllMusic(): void {
        Resources.MenuMusic.stop();
        Resources.GameMusic.stop();
    }
}

// Export singleton instance
export const AudioManager = new AudioManagerClass();
